/**
 * SearchInput — the single, shared search box used across all list/table pages.
 *
 * Standardized toolbar control so every module's search looks and behaves the
 * same: a left-aligned magnifier icon INSIDE the input (Disbursements style),
 * the Loans-module sizing, and a consistent "Search by …" placeholder.
 *
 * Usage:
 *   <SearchInput value={term} onChange={setTerm} placeholder="Search by loan number or borrower" />
 *
 * Props:
 *   value        controlled string value
 *   onChange     (nextValue: string) => void   — receives the raw string
 *   placeholder  placeholder text (prefer "Search by <field> or <field>")
 *   loading      when true, shows a right-aligned spinner (server-search pages)
 *   className    extra classes for the wrapper (e.g. "flex-1" to stretch)
 *   inputClassName extra classes for the <input>
 *   testId       data-testid for the input
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Input } from '../ui/input';
import { Search, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export function SearchInput({
  value,
  onChange,
  placeholder = 'Search by…',
  loading = false,
  className,
  inputClassName,
  testId = 'search-input',
  ...rest
}) {
  return (
    <div className={cn('relative w-full max-w-sm', className)}>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        className={cn('pl-9', loading && 'pr-9', inputClassName)}
        data-testid={testId}
        {...rest}
      />
      {loading && (
        <Loader2
          className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground"
          aria-hidden="true"
        />
      )}
    </div>
  );
}

SearchInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  loading: PropTypes.bool,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  testId: PropTypes.string,
};

export default SearchInput;
